"use client";

import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const signals = [{
  id: "facts",
  chipLabel: "structured facts",
  shortLabel: "Structured Facts",
  counter: "SIGNAL 01 / 04",
  description: "Schema markup, explicit entity definitions, and precise operational details formatted so LLMs parse accurate business information without hallucinating or making assumptions."
}, {
  id: "citations",
  chipLabel: "consistent citations",
  shortLabel: "Consistent Citations",
  counter: "SIGNAL 02 / 04",
  description: "Harmonized Name, Address, Phone, and service parameters across directory networks, map indexes, and web databases that AI models query during real-time retrieval."
}, {
  id: "crawlable",
  chipLabel: "crawlable pages",
  shortLabel: "Crawlable Pages",
  counter: "SIGNAL 03 / 04",
  description: "Clean site architecture, high semantic HTML ratio, and unblocked bot access allowing AI search spiders like GPTBot and ClaudeBot to inspect and index content directly."
}, {
  id: "proof",
  chipLabel: "third-party proof",
  shortLabel: "Third-Party Proof",
  counter: "SIGNAL 04 / 04",
  description: "Verified customer feedback loops, industry backlink profiles, and digital press coverage that prove domain authority to AI algorithms ranking local expertise."
}];
const modelComparison = [{
  label: "Old model",
  text: "ten blue links"
}, {
  label: "New model",
  text: "one synthesized answer"
}, {
  label: "GEO",
  text: "being inside that answer"
}];
export default function Geoservicesfeatures() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: explainerRef,
    isVisible: explainerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [activeSignalId, setActiveSignalId] = useState<string>("facts");
  const activeSignal = signals.find(s => s.id === activeSignalId) || signals[0];
  return <section data-section-id="2110" id="geoservicesfeatures" className="relative overflow-hidden bg-background py-28 md:py-36">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(17 2% 79%)",
      opacity: 0.06,
      fontWeight: 350
    }}>
        01
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(17 2% 79%)",
      opacity: 0.06,
      fontWeight: 350
    }}>
        01
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 uppercase" style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.6875rem",
            fontWeight: 500,
            letterSpacing: "0.18em",
            lineHeight: 1
          }}>
              <span className="text-[hsl(352_54%_33%)]">§ 01</span>
              <span className="text-muted-foreground">—</span>
              <span className="text-muted-foreground">WHAT GEO IS</span>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg bg-[hsl(352_54%_33%)]" />
                <span className="h-1 w-1 rounded-lg bg-[hsl(352_54%_33%)]" />
                <span className="h-1 w-1 rounded-lg bg-[hsl(352_54%_33%)]" />
              </div>
            </div>

            <h2 className="mt-8 text-foreground" style={{
            fontFamily: "var(--font-serif)",
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 350,
            letterSpacing: "-0.02em",
            lineHeight: 1.02
          }}>
              Search stopped listing.{" "}
              <br className="hidden sm:block" />
              It started{" "}
              <em style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 350
            }}>
                answering
              </em>
              .
            </h2>
          </div>

          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default max-w-md text-muted-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.55
          }}>
              Generative Engine Optimization is the practice of structuring a business&apos;s information so language models such as ChatGPT, Gemini, Claude, and Perplexity can read it, trust it, and repeat it accurately inside their generated answers.
            </p>
          </div>
        </div>

        {}
        <div ref={explainerRef} className={`mt-16 transition-all duration-700 ease-out ${explainerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8 items-stretch">
            {}
            <Card className="lg:col-span-7 bg-card border border-border p-6 md:p-8 flex flex-col justify-between">
              <div>
                {}
                <div className="flex items-center justify-between border-b border-border/60 pb-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Sparkles className="size-4 text-[hsl(352_54%_33%)]" />
                    <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                      Anatomy of an AI Answer
                    </span>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground/70">
                    INTERACTIVE DEMO
                  </span>
                </div>

                {}
                <div className="bg-background/80 border border-border/80 p-4 mb-6 rounded-lg font-mono text-sm text-foreground/90">
                  <span className="text-[hsl(352_54%_33%)] font-bold mr-2">&gt;</span>
                  &quot;Who is the top-recommended local service provider in my area with verified ratings?&quot;
                </div>

                {}
                <div className="font-sans text-base md:text-lg leading-relaxed text-foreground/90">
                  <p className="mb-4 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Generated Response:
                  </p>
                  <p className="leading-loose">
                    &quot;When synthesizing recommendations, language models select local businesses by analyzing{" "}
                    {signals.map((signal, index) => {
                    const isActive = activeSignalId === signal.id;
                    return <span key={signal.id} className="inline-block mx-1 my-0.5">
                          <button type="button" onClick={() => setActiveSignalId(signal.id)} aria-pressed={isActive} className={`font-mono text-sm md:text-base px-2 py-1 rounded-lg transition-all duration-200 cursor-pointer border ${isActive ? "bg-[hsl(352_54%_33%/0.18)] text-[hsl(352_54%_45%)] border-[hsl(352_54%_33%)] font-semibold shadow-sm" : "text-foreground border-transparent hover:border-border underline decoration-dotted decoration-[hsl(352_54%_33%)] underline-offset-4 hover:text-[hsl(352_54%_45%)]"}`}>
                            [{signal.chipLabel}]
                          </button>
                          {index < signals.length - 1 ? "," : "."}
                        </span>;
                  })}
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground font-mono">
                <span>Click highlighted signals to inspect ranking indicators</span>
                <span className="text-[hsl(352_54%_33%)]">● LIVE DECODE</span>
              </div>
            </Card>

            {}
            <Card className="lg:col-span-5 bg-card border border-border p-6 md:p-8 flex flex-col justify-between">
              <div key={activeSignal.id} className="transition-all duration-300 ease-out animate-in fade-in slide-in-from-right-2">
                <div className="flex items-center justify-between border-b border-border/60 pb-4 mb-6">
                  <span className="font-mono text-xs uppercase tracking-widest text-[hsl(352_54%_33%)] font-semibold">
                    {activeSignal.counter}
                  </span>
                  <div className="flex items-center gap-1">
                    {signals.map((s, i) => <span key={s.id} data-index={i} className={`h-1.5 rounded-lg transition-all duration-300 ${s.id === activeSignal.id ? "w-5 bg-[hsl(352_54%_33%)]" : "w-1.5 bg-border"}`} />)}
                  </div>
                </div>

                <h3 className="text-foreground text-2xl md:text-3xl font-semibold mb-4" style={{
                fontFamily: "var(--font-serif)"
              }}>
                  {activeSignal.shortLabel}
                </h3>

                <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-sans">
                  {activeSignal.description}
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-border/60 grid grid-cols-2 gap-2">
                {signals.map((signal, i) => {
                const isActive = signal.id === activeSignalId;
                return <button key={signal.id} data-index={i} type="button" onClick={() => setActiveSignalId(signal.id)} aria-pressed={isActive} className={`text-left text-xs font-mono px-3 py-2 rounded-lg transition-colors border ${isActive ? "border-[hsl(352_54%_33%)] bg-[hsl(352_54%_33%/0.1)] text-foreground font-medium" : "border-border/60 text-muted-foreground hover:text-foreground hover:border-border"}`}>
                      0{i + 1}. {signal.shortLabel}
                    </button>;
              })}
              </div>
            </Card>
          </div>

          {}
          <div className="mt-12 border-t border-b border-border py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-border">
              {modelComparison.map((item, i) => <div key={i} data-index={i} className="w-full md:w-1/3 text-center px-4 pt-4 md:pt-0 first:pt-0">
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-1">
                    {item.label}
                  </span>
                  <p className="font-sans text-base md:text-lg font-medium text-foreground">
                    {item.text}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}